'use client';

/**
 * Rx support desk — presentational control center shared by the platform
 * (`/rx`) and partner (`/rx/:partner`) surfaces.
 *
 * Containers own the tRPC hooks (they differ per scope) and hand data +
 * handlers down; this component never imports `trpc`.
 */

import * as React from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Inbox,
  Loader2,
  Mail,
  MailCheck,
  MailWarning,
  RefreshCw,
  Search,
  Send,
  ShieldOff,
  StickyNote,
  UserRound,
} from 'lucide-react';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { RxSupportPatientPanel, type DeskContext, type PatientPanelActions } from './RxSupportPatientPanel';

type AdminOutputs = inferRouterOutputs<AppRouter>['rxSupport']['admin'];
export type DeskConversation = AdminOutputs['listConversations']['items'][number];
export type DeskConversationDetail = AdminOutputs['getConversation'];
export type DeskMailbox = AdminOutputs['mailboxes'][number];
export type DeskCounts = AdminOutputs['counts'];
export type DeskStatus = DeskConversation['status'];
export type DeskStatusFilter = DeskStatus | 'all';

export type RxSupportDeskProps = {
  title: string;
  subtitle: string;
  currentUserId: string | null;

  counts: DeskCounts | undefined;
  mailboxes: DeskMailbox[] | undefined;
  mailboxesLoading: boolean;

  conversations: DeskConversation[] | undefined;
  conversationsLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;

  filters: { status: DeskStatusFilter; storeId: string | null; search: string };
  onFiltersChange: (next: RxSupportDeskProps['filters']) => void;

  selectedId: string | null;
  onSelect: (id: string | null) => void;
  detail: DeskConversationDetail | undefined;
  detailLoading: boolean;

  onReply: (input: { conversationId: string; text: string; closeAfter: boolean }) => Promise<void>;
  onNote: (input: { conversationId: string; text: string }) => Promise<void>;
  onSetStatus: (input: { conversationId: string; status: DeskStatus }) => Promise<void>;
  onAssign: (input: { conversationId: string; assigneeUserId: string | null }) => Promise<void>;
  onRefreshMailbox: (mailboxId: string) => Promise<void>;
  /** Patient, treatments, orders, tracking next to the selected thread. */
  context: DeskContext | undefined;
  contextLoading: boolean;
  /** One-click actions on a treatment. Undefined hides the buttons. */
  caseActions?: PatientPanelActions;
  /** Superadmin-only extras (handler switch). Undefined hides the control. */
  renderMailboxActions?: (mailbox: DeskMailbox) => React.ReactNode;
};

const STATUS_LABEL: Record<DeskStatus, string> = {
  open: 'Open',
  pending: 'Waiting on patient',
  closed: 'Closed',
  spam: 'Spam',
};

const STATUS_DOT: Record<DeskStatus, string> = {
  open: 'bg-amber-500',
  pending: 'bg-sky-500',
  closed: 'bg-emerald-500',
  spam: 'bg-zinc-400',
};

const SOURCE_LABEL: Record<string, string> = {
  email: 'Email',
  portal: 'Portal',
  clinical_network: 'Clinician',
};

/** "3h" / "2d": how long the patient has been waiting on us. */
const waiting = (d: Date | string | null | undefined) =>
  d ? formatDistanceToNowStrict(new Date(d)).replace(/ hours?/, 'h').replace(/ days?/, 'd').replace(/ minutes?/, 'm') : null;

const ago = (d: Date | string | null | undefined) =>
  d ? formatDistanceToNowStrict(new Date(d), { addSuffix: true }) : '—';

const DomainBadge = ({ status, label }: { status: string; label: string }) => {
  const verified = status === 'verified';
  const failed = status === 'failed' || status === 'temporary_failure';
  return (
    <Badge
      variant="outline"
      className={cn(
        'gap-1 font-normal',
        verified && 'border-emerald-300 text-emerald-700',
        failed && 'border-red-300 text-red-700',
        !verified && !failed && 'border-amber-300 text-amber-700',
      )}
    >
      {verified ? <MailCheck className="h-3 w-3" /> : failed ? <MailWarning className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
      {label}: {status.replace('_', ' ')}
    </Badge>
  );
};

export function RxSupportDesk(props: RxSupportDeskProps) {
  const {
    title,
    subtitle,
    counts,
    mailboxes,
    conversations,
    filters,
    onFiltersChange,
    selectedId,
    onSelect,
    detail,
  } = props;

  const [searchDraft, setSearchDraft] = React.useState(filters.search);
  React.useEffect(() => {
    const t = setTimeout(() => {
      if (searchDraft !== filters.search) onFiltersChange({ ...filters, search: searchDraft });
    }, 300);
    return () => clearTimeout(t);
  }, [searchDraft, filters, onFiltersChange]);

  const unhealthyMailboxes = (mailboxes ?? []).filter((m) => m.receivingStatus !== 'verified');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
          <p className="text-muted-foreground text-sm">{subtitle}</p>
        </div>
        <div className="flex gap-2">
          <StatChip label="Open" value={counts?.open} tone="amber" />
          <StatChip label="Waiting" value={counts?.pending} tone="sky" />
          <StatChip label="Closed" value={counts?.closed} tone="emerald" />
        </div>
      </div>

      {unhealthyMailboxes.length > 0 ? (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong>{unhealthyMailboxes.length}</strong> mailbox{unhealthyMailboxes.length > 1 ? 'es are' : ' is'} not
            receiving yet ({unhealthyMailboxes.map((m) => m.address).join(', ')}). Patients writing there bounce until
            DNS verifies — see the Mailboxes tab.
          </div>
        </div>
      ) : null}

      <Tabs defaultValue="inbox">
        <TabsList>
          <TabsTrigger value="inbox" className="gap-2">
            <Inbox className="h-4 w-4" /> Inbox
          </TabsTrigger>
          <TabsTrigger value="mailboxes" className="gap-2">
            <Mail className="h-4 w-4" /> Mailboxes
            {mailboxes ? <Badge variant="secondary" className="ml-1">{mailboxes.length}</Badge> : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="mt-4">
          <div className="grid gap-4 lg:grid-cols-[minmax(300px,2fr)_3fr] 2xl:grid-cols-[minmax(300px,2fr)_3fr_minmax(300px,2fr)]">
            {/* List */}
            <div className="flex min-h-[70vh] flex-col rounded-lg border">
              <div className="flex flex-wrap gap-2 border-b p-3">
                <div className="relative min-w-[160px] flex-1">
                  <Search className="text-muted-foreground absolute left-2 top-2.5 h-4 w-4" />
                  <Input
                    value={searchDraft}
                    onChange={(e) => setSearchDraft(e.target.value)}
                    placeholder="Search email, subject, TGD-…"
                    className="pl-8"
                  />
                </div>
                <Select
                  value={filters.status}
                  onValueChange={(v) => onFiltersChange({ ...filters, status: v as DeskStatusFilter })}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="pending">Waiting on patient</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="spam">Spam</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={filters.storeId ?? '__all'}
                  onValueChange={(v) => onFiltersChange({ ...filters, storeId: v === '__all' ? null : v })}
                >
                  <SelectTrigger className="w-[170px]">
                    <SelectValue placeholder="All brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All brands</SelectItem>
                    {(mailboxes ?? []).map((m) => (
                      <SelectItem key={m.id} value={m.storeId}>
                        {m.displayName.replace(/ Care$/, '')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 overflow-y-auto">
                {props.conversationsLoading && !conversations ? (
                  <Centered>
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </Centered>
                ) : (conversations?.length ?? 0) === 0 ? (
                  <Centered>
                    <Inbox className="text-muted-foreground h-8 w-8" />
                    <p className="text-muted-foreground text-sm">Nothing here. Enjoy the quiet.</p>
                  </Centered>
                ) : (
                  <ul className="divide-y">
                    {conversations!.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => onSelect(c.id)}
                          className={cn(
                            'hover:bg-muted/60 flex w-full flex-col gap-1 px-3 py-2.5 text-left transition-colors',
                            selectedId === c.id && 'bg-muted',
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <span className={cn('h-2 w-2 shrink-0 rounded-full', STATUS_DOT[c.status])} />
                            <span className="truncate text-sm font-medium">{c.contactName || c.contactEmail}</span>
                            <span className="text-muted-foreground ml-auto shrink-0 text-xs">{ago(c.lastMessageAt)}</span>
                          </div>
                          <div className="truncate text-sm">{c.subject || '(no subject)'}</div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="h-5 px-1.5 text-[11px] font-normal">
                              {c.brandName.replace(/ Care$/, '')}
                            </Badge>
                            {c.source !== 'email' ? (
                              <Badge variant="outline" className="h-5 px-1.5 text-[11px] font-normal">
                                {SOURCE_LABEL[c.source] ?? c.source}
                              </Badge>
                            ) : null}
                            <span className="text-muted-foreground font-mono text-[11px]">{c.reference}</span>
                            {c.status === 'open' && c.lastInboundAt ? (
                              <span className="text-[11px] font-medium text-amber-700">waiting {waiting(c.lastInboundAt)}</span>
                            ) : null}
                            {c.assigneeUserId ? (
                              <UserRound className="text-muted-foreground ml-auto h-3.5 w-3.5" />
                            ) : null}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {props.hasMore ? (
                <div className="border-t p-2">
                  <Button variant="ghost" size="sm" className="w-full" onClick={props.onLoadMore}>
                    Load more
                  </Button>
                </div>
              ) : null}
            </div>

            {/* Detail */}
            <div className="flex min-h-[70vh] flex-col rounded-lg border">
              {!selectedId ? (
                <Centered>
                  <Mail className="text-muted-foreground h-8 w-8" />
                  <p className="text-muted-foreground text-sm">Select a conversation.</p>
                </Centered>
              ) : props.detailLoading || !detail ? (
                <Centered>
                  <Loader2 className="h-5 w-5 animate-spin" />
                </Centered>
              ) : (
                <ConversationPane
                  detail={detail}
                  currentUserId={props.currentUserId}
                  onReply={props.onReply}
                  onNote={props.onNote}
                  onSetStatus={props.onSetStatus}
                  onAssign={props.onAssign}
                />
              )}
            </div>

            {/* Patient: treatments, orders, tracking, renewals */}
            <div className="flex flex-col rounded-lg border lg:col-span-2 2xl:col-span-1 2xl:min-h-[70vh]">
              <div className="border-b px-4 py-3 text-sm font-semibold">Patient</div>
              {selectedId ? (
                <RxSupportPatientPanel context={props.context} loading={props.contextLoading} actions={props.caseActions} />
              ) : (
                <Centered>
                  <p className="text-muted-foreground text-sm">Select a conversation.</p>
                </Centered>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="mailboxes" className="mt-4">
          <MailboxTable
            mailboxes={mailboxes}
            loading={props.mailboxesLoading}
            onRefresh={props.onRefreshMailbox}
            renderActions={props.renderMailboxActions}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------

const Centered = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8">{children}</div>
);

const StatChip = ({ label, value, tone }: { label: string; value: number | undefined; tone: 'amber' | 'sky' | 'emerald' }) => (
  <div
    className={cn(
      'rounded-md border px-3 py-1.5 text-sm',
      tone === 'amber' && 'border-amber-200 bg-amber-50 text-amber-900',
      tone === 'sky' && 'border-sky-200 bg-sky-50 text-sky-900',
      tone === 'emerald' && 'border-emerald-200 bg-emerald-50 text-emerald-900',
    )}
  >
    <span className="font-semibold">{value ?? '–'}</span> <span className="opacity-80">{label}</span>
  </div>
);

function ConversationPane({
  detail,
  currentUserId,
  onReply,
  onNote,
  onSetStatus,
  onAssign,
}: {
  detail: DeskConversationDetail;
  currentUserId: string | null;
  onReply: RxSupportDeskProps['onReply'];
  onNote: RxSupportDeskProps['onNote'];
  onSetStatus: RxSupportDeskProps['onSetStatus'];
  onAssign: RxSupportDeskProps['onAssign'];
}) {
  const { conversation, mailbox, messages } = detail;
  const [draft, setDraft] = React.useState('');
  const [mode, setMode] = React.useState<'reply' | 'note'>('reply');
  const [busy, setBusy] = React.useState<string | null>(null);
  const bottomRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setDraft('');
    setMode('reply');
  }, [conversation.id]);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, conversation.id]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  };

  const submit = (closeAfter: boolean) => {
    const text = draft.trim();
    if (!text) return;
    void run('send', async () => {
      if (mode === 'note') await onNote({ conversationId: conversation.id, text });
      else await onReply({ conversationId: conversation.id, text, closeAfter });
      setDraft('');
    });
  };

  const canSend = mailbox.domainStatus === 'verified';
  const isMine = currentUserId && conversation.assigneeUserId === currentUserId;

  return (
    <>
      <div className="space-y-2 border-b p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold">{conversation.subject || '(no subject)'}</h3>
            <p className="text-muted-foreground text-sm">
              {conversation.contactName ? `${conversation.contactName} · ` : ''}
              <span className="font-mono">{conversation.contactEmail}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono">{conversation.reference}</Badge>
            <Badge variant="secondary">{STATUS_LABEL[conversation.status]}</Badge>
          </div>
        </div>
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span>
            via <span className="font-mono">{mailbox.address}</span>
          </span>
          <span>· store {conversation.storeName}</span>
          <span>· from {SOURCE_LABEL[conversation.source]?.toLowerCase() ?? conversation.source}</span>
          {conversation.customerId ? <span>· known customer</span> : <span>· unknown sender</span>}
          <span>· {conversation.messageCount} message{conversation.messageCount === 1 ? '' : 's'}</span>
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          {currentUserId ? (
            <Button
              size="sm"
              variant={isMine ? 'secondary' : 'outline'}
              disabled={busy !== null}
              onClick={() =>
                run('assign', () =>
                  onAssign({ conversationId: conversation.id, assigneeUserId: isMine ? null : currentUserId }),
                )
              }
            >
              <UserRound className="mr-1.5 h-3.5 w-3.5" />
              {isMine ? 'Assigned to me' : conversation.assigneeUserId ? 'Take over' : 'Assign to me'}
            </Button>
          ) : null}
          {conversation.status !== 'closed' ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy !== null}
              onClick={() => run('status', () => onSetStatus({ conversationId: conversation.id, status: 'closed' }))}
            >
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Close
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={busy !== null}
              onClick={() => run('status', () => onSetStatus({ conversationId: conversation.id, status: 'open' }))}
            >
              <Archive className="mr-1.5 h-3.5 w-3.5" /> Reopen
            </Button>
          )}
          {conversation.status !== 'spam' ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy !== null}
              onClick={() => run('status', () => onSetStatus({ conversationId: conversation.id, status: 'spam' }))}
            >
              <ShieldOff className="mr-1.5 h-3.5 w-3.5" /> Spam
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="space-y-2 border-t p-3">
        {!canSend ? (
          <p className="flex items-center gap-2 text-xs text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5" /> Sending domain not verified yet — you can leave notes, but replies
            will fail until <span className="font-mono">{mailbox.domain}</span> verifies.
          </p>
        ) : null}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={mode === 'reply' ? 'secondary' : 'ghost'}
            onClick={() => setMode('reply')}
          >
            <Send className="mr-1.5 h-3.5 w-3.5" /> Reply as {mailbox.displayName}
          </Button>
          <Button size="sm" variant={mode === 'note' ? 'secondary' : 'ghost'} onClick={() => setMode('note')}>
            <StickyNote className="mr-1.5 h-3.5 w-3.5" /> Internal note
          </Button>
        </div>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={5}
          placeholder={
            mode === 'note'
              ? 'Visible to the team only…'
              : `Write to ${conversation.contactName || conversation.contactEmail}. Sent from ${mailbox.address}.`
          }
          className={cn(mode === 'note' && 'border-amber-300 bg-amber-50/50')}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(false);
          }}
        />
        <div className="flex items-center justify-end gap-2">
          <span className="text-muted-foreground mr-auto text-xs">⌘↵ to send</span>
          {mode === 'reply' ? (
            <Button
              size="sm"
              variant="outline"
              disabled={!draft.trim() || busy !== null || !canSend}
              onClick={() => submit(true)}
            >
              Send &amp; close
            </Button>
          ) : null}
          <Button
            size="sm"
            disabled={!draft.trim() || busy !== null || (mode === 'reply' && !canSend)}
            onClick={() => submit(false)}
          >
            {busy === 'send' ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            {mode === 'note' ? 'Add note' : 'Send reply'}
          </Button>
        </div>
      </div>
    </>
  );
}

function MessageBubble({ message }: { message: DeskConversationDetail['messages'][number] }) {
  const isNote = message.direction === 'note';
  const isOut = message.direction === 'outbound';
  return (
    <div className={cn('flex', isOut && 'justify-end')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg border px-3 py-2 text-sm',
          isNote && 'border-amber-300 bg-amber-50',
          isOut && !isNote && 'bg-primary/5 border-primary/20',
          !isOut && !isNote && 'bg-card',
        )}
      >
        <div className="text-muted-foreground mb-1 flex items-center gap-2 text-xs">
          {isNote ? <StickyNote className="h-3 w-3" /> : null}
          <span className="font-medium">
            {isNote
              ? `Note · ${message.authorName ?? 'team'}`
              : isOut
                ? `${message.fromName ?? message.fromAddress}${message.authorName ? ` (${message.authorName})` : ''}`
                : message.fromName
                  ? `${message.fromName} <${message.fromAddress}>`
                  : message.fromAddress}
          </span>
          <span className="ml-auto">{ago(message.sentAt)}</span>
        </div>
        <div className="whitespace-pre-wrap break-words">{message.textBody ?? stripHtml(message.htmlBody) ?? ''}</div>
        {message.attachments.length > 0 ? (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {message.attachments.map((a) => (
              <li key={a.id}>
                {a.downloadUrl ? (
                  <a
                    href={a.downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="bg-muted hover:bg-muted/70 rounded px-2 py-0.5 text-xs underline-offset-2 hover:underline"
                  >
                    {a.filename}
                  </a>
                ) : (
                  <span className="bg-muted rounded px-2 py-0.5 text-xs">{a.filename}</span>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

const stripHtml = (html: string | null): string | null => {
  if (!html) return null;
  if (typeof window === 'undefined') return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent?.trim() ?? null;
};

function MailboxTable({
  mailboxes,
  loading,
  onRefresh,
  renderActions,
}: {
  mailboxes: DeskMailbox[] | undefined;
  loading: boolean;
  onRefresh: (mailboxId: string) => Promise<void>;
  renderActions?: (mailbox: DeskMailbox) => React.ReactNode;
}) {
  const [refreshing, setRefreshing] = React.useState<string | null>(null);
  if (loading && !mailboxes) {
    return (
      <Centered>
        <Loader2 className="h-5 w-5 animate-spin" />
      </Centered>
    );
  }
  if ((mailboxes?.length ?? 0) === 0) {
    return (
      <Centered>
        <Mail className="text-muted-foreground h-8 w-8" />
        <p className="text-muted-foreground text-sm">
          No brand mailbox yet. Publishing an Rx storefront provisions <span className="font-mono">care@&lt;slug&gt;</span>{' '}
          automatically.
        </p>
      </Centered>
    );
  }
  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Brand</TableHead>
            <TableHead>Address</TableHead>
            <TableHead>Domain</TableHead>
            <TableHead>Handled by</TableHead>
            <TableHead className="text-right">Open</TableHead>
            <TableHead>Oldest open</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {mailboxes!.map((m) => (
            <TableRow key={m.id}>
              <TableCell>
                <div className="font-medium">{m.displayName.replace(/ Care$/, '')}</div>
                <div className="text-muted-foreground text-xs">
                  {m.storeName}
                  {m.partner ? ` · partner ${m.partner}` : ''}
                </div>
              </TableCell>
              <TableCell>
                <div className="font-mono text-sm">{m.address}</div>
                <div className="text-muted-foreground text-xs">
                  {m.kind === 'custom_domain' ? 'merchant domain' : 'platform subdomain'}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  <DomainBadge status={m.domainStatus} label="send" />
                  <DomainBadge status={m.receivingStatus} label="receive" />
                </div>
              </TableCell>
              <TableCell>
                <Badge variant={m.handledBy === 'platform' ? 'secondary' : 'outline'}>
                  {m.handledBy === 'platform' ? 'TGD Care desk' : `partner ${m.handlerPartner}`}
                </Badge>
              </TableCell>
              <TableCell className="text-right font-medium">{m.openCount}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{ago(m.oldestOpenAt)}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  {renderActions?.(m)}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={refreshing !== null}
                    onClick={() => {
                      setRefreshing(m.id);
                      void onRefresh(m.id).finally(() => setRefreshing(null));
                    }}
                    title="Re-check DNS with Resend"
                  >
                    <RefreshCw className={cn('h-3.5 w-3.5', refreshing === m.id && 'animate-spin')} />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
